import { useState } from "react";
import { latestFeeds } from "@/mocks/home";
import SectionHeading from "@/pages/home/components/SectionHeading";

const toneMap: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
  foreground: "bg-background-200 text-foreground-800",
};

type FeedTab = "fresh" | "hot";

export default function LatestFeeds() {
  const [tab, setTab] = useState<FeedTab>("fresh");
  const items = tab === "fresh" ? latestFeeds.fresh : latestFeeds.hot;

  return (
    <section id="latest" className="w-full bg-background-100/70 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-6">
        <SectionHeading
          eyebrow="最新收录"
          title="刚刚归位的条目，按收录顺序往下排"
          desc="新增、补齐、去重的动作都会在这里留下记录，顺着往下看就知道仓库最近动了哪些内容。"
          linkLabel="查看更新日志"
          linkHref="#ranking"
        />

        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-50 px-1 py-1">
            <button
              type="button"
              onClick={() => setTab("fresh")}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                tab === "fresh"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              <i className="ri-time-line" />
              最新收录
            </button>
            <button
              type="button"
              onClick={() => setTab("hot")}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                tab === "hot"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              <i className="ri-fire-line" />
              热度优先
            </button>
          </div>
          <span className="flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-refresh-line text-primary-500" />
            每 15 分钟巡检一次新条目
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
          {items.map((item, index) => (
            <a
              key={item.id}
              href="#collections"
              className="group flex items-start gap-3.5 rounded-lg border border-background-200/80 bg-background-50 p-3.5 transition-colors hover:border-primary-300 cursor-pointer md:gap-4 md:p-4"
            >
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${
                  toneMap[item.tone] ?? toneMap.primary
                }`}
              >
                <i className={`${item.icon} text-lg`} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-medium leading-relaxed text-foreground-950 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </h3>
                  <span className="shrink-0 font-label text-[11px] text-foreground-400">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-links-line" />
                    {item.source}
                  </span>
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900"
                    >
                      {tag}
                    </span>
                  ))}
                  <span className="flex items-center gap-1 text-foreground-400">
                    <i className="ri-history-line" />
                    {item.time}
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}